// src/pages/GraficoTendenciaMensal.jsx

import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Divider, Dialog, DialogTitle, DialogContent, IconButton, Stack, Chip } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CloseIcon from "@mui/icons-material/Close";

function N(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function sumWeeks(row) {
  return N(row?.GASTO_REAL);
}

function resolveMonthYear(row) {
  let month = null;
  let year = null;
  if (row?.MES != null) month = N(row.MES);
  if (row?.mes != null) month = N(row.mes);
  if (row?.ANO != null) year = N(row.ANO);
  if (row?.ano != null) year = N(row.ano);

  const dateCandidate = row?.DATA || row?.data || row?.DATA_REF || row?.competencia || row?.COMPETENCIA;
  if ((month == null || year == null) && dateCandidate) {
    const d = dayjs(dateCandidate);
    if (d.isValid()) {
      if (month == null) month = d.month() + 1;
      if (year == null) year = d.year();
    }
  }
  if (!Number.isInteger(month) || month < 1 || month > 12) month = null;
  if (!Number.isInteger(year)) year = null;
  return { month, year };
}

function currencyBR(value) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 }).format(N(value));
}

function percentBR(value) {
  return `${N(value).toFixed(1)}%`;
}

const MESES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
const MESES_EXTENSO = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

function CustomMonthTick({ x, y, payload, fill }) {
  return (
    <text
      x={x}
      y={y}
      dy={16}
      textAnchor="middle"
      fill={fill}
      className="notranslate"
      translate="no"
      style={{ userSelect: "none" }}
    >
      {payload?.value}
    </text>
  );
}

export default function GraficoTendenciaMensal({ rows = [], startDate, kmAcopPorMes = {} }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const azul = theme.palette.mode === "dark" ? "#4ea1ff" : "#1f77b4";
  const verde = theme.palette.mode === "dark" ? "#6fd18d" : "#2ca02c";
  const cinzaPct = theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[700];

  const selectedYear = useMemo(() => {
    const d = dayjs(startDate);
    return d.isValid() ? d.year() : dayjs().year();
  }, [startDate]);

  const mesesParaExibir = useMemo(() => {
    const anoAtual = dayjs().year();
    const mesAtual = dayjs().month() + 1;
    return selectedYear === anoAtual ? mesAtual : 12;
  }, [selectedYear]);

  const base = useMemo(() => {
    const len = Math.max(1, Math.min(12, mesesParaExibir));
    return Array.from({ length: len }, (_, i) => ({
      mesIdx: i + 1,
      label: MESES[i],
      real: 0,
      immuKm: 0,
      km: N(kmAcopPorMes[i + 1]),
    }));
  }, [mesesParaExibir, kmAcopPorMes]);

  const data = useMemo(() => {
    const byMonth = base.map((m) => ({ ...m }));

    for (const row of rows) {
      const { month, year } = resolveMonthYear(row);
      if (year !== selectedYear || month == null) continue;
      if (month < 1 || month > byMonth.length) continue;
      const idx = month - 1;
      byMonth[idx].real += sumWeeks(row);
      byMonth[idx].immuKm += N(row?.IMMU);
    }

    return byMonth.map((m) => {
      const custoRemu = N(m.immuKm) * N(m.km);
      const difPct = custoRemu > 0 ? ((N(m.real) - custoRemu) / custoRemu) * 100 : 0;
      return {
        mes: m.label,
        mesIdx: m.mesIdx,
        gastoReal: Number(m.real.toFixed(2)),
        custoRemu: Number(custoRemu.toFixed(2)),
        difPct: Number(difPct.toFixed(1)),
      };
    });
  }, [rows, base, selectedYear]);

  const hasAny = data.some((d) => d.gastoReal > 0 || d.custoRemu > 0);

  const resumoKpis = useMemo(() => {
    if (!hasAny) return null;
    const totalReal = data.reduce((acc, d) => acc + (d.gastoReal || 0), 0);
    const totalRemu = data.reduce((acc, d) => acc + (d.custoRemu || 0), 0);
    const deltaAbs = totalReal - totalRemu;
    const deltaPct = totalRemu > 0 ? (deltaAbs / totalRemu) * 100 : 0;

    let pior = null;
    let melhor = null;
    data.forEach((d) => {
      if (pior === null || d.difPct > pior.difPct) pior = d;
      if (melhor === null || d.difPct < melhor.difPct) melhor = d;
    });

    const first = data[0];
    const last = data[data.length - 1];
    const slopeReal = (last.gastoReal - first.gastoReal) / Math.max(1, data.length - 1);
    const slopeRemu = (last.custoRemu - first.custoRemu) / Math.max(1, data.length - 1);

    const mediaDeltaAbsMes = deltaAbs / data.length;
    const mediaRemuMes = totalRemu / data.length;
    const projDeltaAbsAnual = mediaDeltaAbsMes * 12;
    const projRemuAnual = mediaRemuMes * 12;
    const projPctAnual = projRemuAnual > 0 ? (projDeltaAbsAnual / projRemuAnual) * 100 : 0;

    return {
      totalReal,
      totalRemu,
      deltaAbs,
      deltaPct,
      pior,
      melhor,
      slopeReal,
      slopeRemu,
      mediaDeltaAbsMes,
      projDeltaAbsAnual,
      projPctAnual,
      meses: data.length,
      ate: MESES[data.length - 1],
    };
  }, [data, hasAny]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Typography variant="h6" align="center" sx={{ fontWeight: 700, mb: 1 }}>
        ANÁLISE DE TENDÊNCIA MENSAL: GASTO REAL VS. CUSTO REMUNERADO — {selectedYear}
      </Typography>

      {!hasAny ? (
        <Box sx={{ py: 6, textAlign: "center", color: theme.palette.text.secondary }}>
          <Typography variant="caption">
            Verifique se a base possui valores de GASTO_REAL, IMMU e KM para os meses exibidos.
          </Typography>
        </Box>
      ) : (
        <Box className="notranslate" translate="no" sx={{ height: 380 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="mes" interval={0} tick={<CustomMonthTick fill={theme.palette.text.primary} />} />
              <YAxis yAxisId="left" tickFormatter={(v) => new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(v)} tick={{ fontSize: 12, fill: theme.palette.text.primary }} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12, fill: theme.palette.text.primary }} />
              <Tooltip formatter={(value, name) => { if (name === "Diferença %") return [percentBR(value), name]; return [currencyBR(value), name]; }} labelFormatter={(label) => `${label}/${selectedYear}`} contentStyle={{ background: theme.palette.background.paper, borderColor: theme.palette.divider }} itemStyle={{ color: theme.palette.text.primary }} labelStyle={{ color: theme.palette.text.secondary }} />
              <Legend wrapperStyle={{ color: theme.palette.text.primary }} />
              <Line yAxisId="left" type="monotone" dataKey="gastoReal" name="Gasto Real" stroke={azul} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line yAxisId="left" type="monotone" dataKey="custoRemu" name="Custo Remunerado IMMU" stroke={verde} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line yAxisId="right" type="monotone" dataKey="difPct" name="Diferença %" stroke={cinzaPct} strokeDasharray="5 3" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
              <ReferenceLine yAxisId="right" y={0} stroke={theme.palette.divider} strokeDasharray="2 2" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {hasAny && resumoKpis && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: theme.palette.text.secondary }}>
          <InsightsOutlinedIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Análise Técnica Automática</Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setOpen(true)} aria-label="abrir análise">
            <Typography variant="body2" sx={{ textDecoration: "underline" }}>ver detalhes</Typography>
          </IconButton>
        </Stack>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}>
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InsightsOutlinedIcon />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Análise Técnica Automática — {selectedYear}</Typography>
          </Stack>
          <IconButton onClick={() => setOpen(false)} aria-label="Fechar" sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {hasAny && resumoKpis ? (
            <Box>
              {/* KPIs resumidos em chips */}
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip label={`Real: ${currencyBR(resumoKpis.totalReal)}`} />
                <Chip label={`Remunerado: ${currencyBR(resumoKpis.totalRemu)}`} />
                <Chip label={`Desvio: ${currencyBR(resumoKpis.deltaAbs)} (${percentBR(resumoKpis.deltaPct)})`} />
                <Chip label={`Período: ${resumoKpis.meses} mês(es)`} />
              </Stack>

              {/* Análise no estilo do anexo: mais abrangente e narrativa */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Análise Consolidada ({resumoKpis.meses} mês(es))
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                O Gasto Real totalizou {currencyBR(resumoKpis.totalReal)}, {resumoKpis.deltaAbs >= 0 ? 'superando' : 'ficando abaixo de'} o Custo Remunerado IMMU ({currencyBR(resumoKpis.totalRemu)}) em {currencyBR(Math.abs(resumoKpis.deltaAbs))}. Essa diferença representa {percentBR(Math.abs(resumoKpis.deltaPct))} {resumoKpis.deltaAbs >= 0 ? 'acima' : 'abaixo'} do orçamento remunerado acumulado, indicando {Math.abs(resumoKpis.deltaPct) >= 10 ? 'um desvio significativo' : 'um desvio moderado'} e {Math.abs(resumoKpis.slopeReal - resumoKpis.slopeRemu) < 1 ? 'relativamente estável' : (resumoKpis.slopeReal > resumoKpis.slopeRemu ? 'tendência de pressão nos gastos' : 'alívio relativo nos gastos')} ao longo do período.
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Pontos Críticos e Oportunidades
              </Typography>
              {resumoKpis.pior ? (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  • <b>Pior Desempenho</b> ({MESES_EXTENSO[resumoKpis.pior.mesIdx - 1]}): maior diferença percentual de {percentBR(resumoKpis.pior.difPct)} — Real {currencyBR(resumoKpis.pior.gastoReal)} vs. Remunerado {currencyBR(resumoKpis.pior.custoRemu)}. Este pico negativo sugere investigação de variações de preço, consumo atípico, contratos e eventuais lançamentos contábeis fora do padrão.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  • Não há mês crítico identificado no período.
                </Typography>
              )}
              {resumoKpis.melhor ? (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                  • <b>Melhor Desempenho</b> ({MESES_EXTENSO[resumoKpis.melhor.mesIdx - 1]}): menor diferença percentual de {percentBR(resumoKpis.melhor.difPct)} — Real {currencyBR(resumoKpis.melhor.gastoReal)} vs. Remunerado {currencyBR(resumoKpis.melhor.custoRemu)}. Este comportamento próximo ao orçamento pode servir como referência para replicar práticas (contratos, processos e alocação de recursos) nos meses seguintes.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                  • Não há destaque positivo identificado no período.
                </Typography>
              )}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Tendência e Projeção
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                A tendência geral indica gasto real {resumoKpis.slopeReal > 0 ? 'em aceleração' : resumoKpis.slopeReal < 0 ? 'em queda' : 'estável'} e custo remunerado {resumoKpis.slopeRemu > 0 ? 'em aceleração' : resumoKpis.slopeRemu < 0 ? 'em queda' : 'estável'} no período observado. Mantida a média mensal do desvio ({currencyBR(resumoKpis.mediaDeltaAbsMes)} por mês), a projeção anual aponta um desvio de {currencyBR(resumoKpis.projDeltaAbsAnual)} ({percentBR(resumoKpis.projPctAnual)}) contra um orçamento remunerado anual estimado pela média do período. Caso essa trajetória se mantenha, o impacto financeiro tenderá {resumoKpis.projDeltaAbsAnual >= 0 ? 'a pressionar' : 'a aliviar'} os resultados até o fechamento do ano.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Sem dados suficientes para análise.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
